import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { Table } from '../../../shared/components/Table';
import { Toggle } from '../../../shared/components/Toggle';
import Button from '../../../shared/components/Button';
import { CurationStatusBadge } from '../components/CurationStatusBadge';
import { Stars } from '../components/Stars';
import { ReviewDetailModal } from '../components/ReviewDetailModal';
import { TestimonialDetailModal } from '../components/TestimonialDetailModal';
import { useReviews, useTestimonials, useSetReviewStatus, useSetReviewFeatured, useSetTestimonialStatus, useSetTestimonialFeatured } from '../hooks/useReviews';
import { canFeature, excerpt } from '../status';
import type { CurationStatus, Review, Testimonial } from '../api/reviewsApi';

type Tab = 'reviews' | 'testimonials';

const STATUS_FILTERS: { value: '' | CurationStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

const emptyBox =
  'text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]';

const pillBase = 'text-[13px] px-3 py-1.5 rounded-full transition-colors';
const pillActive = 'bg-[#28282A] text-white';
const pillInactive = 'bg-muted text-muted-foreground hover:bg-muted';

export function ReviewsModerationPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.PRODUCT_UPDATE);

  const [tab, setTab] = useState<Tab>('reviews');
  const [status, setStatus] = useState<'' | CurationStatus>('PENDING');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;
  const [openReview, setOpenReview] = useState<Review | null>(null);
  const [openTestimonial, setOpenTestimonial] = useState<Testimonial | null>(null);

  const listParams = {
    status: status || undefined,
    isFeatured: featuredOnly || undefined,
    page,
    limit,
  };

  const reviewsQuery = useReviews(listParams);
  const testimonialsQuery = useTestimonials(listParams);

  const setReviewStatus = useSetReviewStatus();
  const setReviewFeatured = useSetReviewFeatured();
  const setTestimonialStatus = useSetTestimonialStatus();
  const setTestimonialFeatured = useSetTestimonialFeatured();

  const changeTab = (next: Tab) => {
    setTab(next);
    setPage(1);
  };
  const changeStatus = (next: '' | CurationStatus) => {
    setStatus(next);
    setPage(1);
  };
  const changeFeaturedOnly = (next: boolean) => {
    setFeaturedOnly(next);
    setPage(1);
  };

  const reviewColumns = useMemo<ColumnDef<Review>[]>(
    () => [
      {
        id: 'rating',
        header: 'Rating',
        accessorFn: (row) => row.rating,
        cell: ({ row }) => <Stars rating={row.original.rating} />,
      },
      {
        id: 'excerpt',
        header: 'Review',
        cell: ({ row }) => (
          <div>
            {row.original.title && <div className="font-medium text-foreground">{row.original.title}</div>}
            <div className="text-muted-foreground text-[13px]">{excerpt(row.original.body)}</div>
          </div>
        ),
      },
      {
        id: 'customer',
        header: 'Customer',
        cell: ({ row }) => (
          <div>
            <div className="text-foreground">{row.original.customerName}</div>
            {row.original.verifiedPurchase && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700 mt-0.5">
                Verified purchase
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'product',
        header: 'Product',
        cell: ({ row }) => (
          <Link
            to={`/products/edit/${row.original.productId}`}
            onClick={(e) => e.stopPropagation()}
            className="text-foreground underline hover:no-underline"
          >
            {row.original.productId.slice(0, 8)}
          </Link>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status,
        cell: ({ row }) => <CurationStatusBadge status={row.original.status} />,
      },
      {
        id: 'featured',
        header: 'Featured',
        cell: ({ row }) => {
          const review = row.original;
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <Toggle
                checked={review.isFeatured}
                disabled={!canManage || !canFeature(review.status)}
                title={canFeature(review.status) ? undefined : 'Approve first to feature'}
                onChange={(checked) => {
                  setReviewFeatured.mutate({ id: review.id, isFeatured: checked });
                }}
              />
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          if (!canManage) return null;
          const review = row.original;
          return (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="sm"
                disabled={review.status === 'APPROVED'}
                onClick={() => setReviewStatus.mutate({ id: review.id, status: 'APPROVED' })}
              >
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={review.status === 'REJECTED'}
                onClick={() => setReviewStatus.mutate({ id: review.id, status: 'REJECTED' })}
              >
                Reject
              </Button>
            </div>
          );
        },
      },
    ],
    [canManage, setReviewFeatured, setReviewStatus]
  );

  const testimonialColumns = useMemo<ColumnDef<Testimonial>[]>(
    () => [
      {
        id: 'rating',
        header: 'Rating',
        accessorFn: (row) => row.rating ?? -1,
        cell: ({ row }) => <Stars rating={row.original.rating} />,
      },
      {
        id: 'excerpt',
        header: 'Testimonial',
        cell: ({ row }) => (
          <div>
            {row.original.title && <div className="font-medium text-foreground">{row.original.title}</div>}
            <div className="text-muted-foreground text-[13px]">{excerpt(row.original.body)}</div>
          </div>
        ),
      },
      {
        id: 'customer',
        header: 'Customer',
        cell: ({ row }) => (
          <div>
            <div className="text-foreground">{row.original.customerName}</div>
            {row.original.customerRole && (
              <div className="text-muted-foreground text-[12px]">{row.original.customerRole}</div>
            )}
          </div>
        ),
      },
      {
        id: 'product',
        header: 'Product',
        cell: ({ row }) =>
          row.original.productId ? (
            <Link
              to={`/products/edit/${row.original.productId}`}
              onClick={(e) => e.stopPropagation()}
              className="text-foreground underline hover:no-underline"
            >
              {row.original.productId.slice(0, 8)}
            </Link>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status,
        cell: ({ row }) => <CurationStatusBadge status={row.original.status} />,
      },
      {
        id: 'featured',
        header: 'Featured',
        cell: ({ row }) => {
          const testimonial = row.original;
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <Toggle
                checked={testimonial.isFeatured}
                disabled={!canManage || !canFeature(testimonial.status)}
                title={canFeature(testimonial.status) ? undefined : 'Approve first to feature'}
                onChange={(checked) => {
                  setTestimonialFeatured.mutate({ id: testimonial.id, isFeatured: checked });
                }}
              />
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          if (!canManage) return null;
          const testimonial = row.original;
          return (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="sm"
                disabled={testimonial.status === 'APPROVED'}
                onClick={() => setTestimonialStatus.mutate({ id: testimonial.id, status: 'APPROVED' })}
              >
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={testimonial.status === 'REJECTED'}
                onClick={() => setTestimonialStatus.mutate({ id: testimonial.id, status: 'REJECTED' })}
              >
                Reject
              </Button>
            </div>
          );
        },
      },
    ],
    [canManage, setTestimonialFeatured, setTestimonialStatus]
  );

  const activeQuery = tab === 'reviews' ? reviewsQuery : testimonialsQuery;
  const total = activeQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <div className="mb-6">
        <h1 className="text-[23px] font-bold text-foreground">Reviews & testimonials</h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          Only approved reviews and testimonials appear on the storefront; featured items are shown first.
        </p>
      </div>

      {!selectedTenantId ? (
        <div className={emptyBox}>Select a merchant to moderate their reviews and testimonials.</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex flex-wrap gap-1">
              {(['reviews', 'testimonials'] as Tab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => changeTab(t)}
                  className={`${pillBase} ${tab === t ? pillActive : pillInactive}`}
                >
                  {t === 'reviews' ? 'Reviews' : 'Testimonials'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex flex-wrap gap-1">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value || 'all'}
                  onClick={() => changeStatus(f.value)}
                  className={`${pillBase} ${status === f.value ? pillActive : pillInactive}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="ml-auto">
              <Toggle label="Featured only" checked={featuredOnly} onChange={changeFeaturedOnly} />
            </div>
          </div>

          {activeQuery.error ? (
            <div className="text-center py-16 text-red-500">
              Failed to load {tab === 'reviews' ? 'reviews' : 'testimonials'}. Please try again.
            </div>
          ) : (
            <>
              {tab === 'reviews' ? (
                <Table
                  data={reviewsQuery.data?.data ?? []}
                  columns={reviewColumns}
                  isLoading={reviewsQuery.isLoading}
                  enableSorting={false}
                  storageKey="reviews-table"
                  onRowClick={(row) => setOpenReview(row)}
                />
              ) : (
                <Table
                  data={testimonialsQuery.data?.data ?? []}
                  columns={testimonialColumns}
                  isLoading={testimonialsQuery.isLoading}
                  enableSorting={false}
                  storageKey="testimonials-table"
                  onRowClick={(row) => setOpenTestimonial(row)}
                />
              )}

              {/* Prev/next pager (same pattern as features/payments) — the shared Pagination
                  component is load-more style, which doesn't fit a page-replacing moderation queue. */}
              <div className="flex items-center justify-between px-5 py-3 border-t border-border">
                <span className="text-xs text-muted-foreground">
                  {total} {tab === 'reviews' ? 'review' : 'testimonial'}{total === 1 ? '' : 's'} · page {page} of {totalPages}{activeQuery.isFetching ? ' · …' : ''}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((n) => Math.max(1, n - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1.5 text-sm border border-border rounded-lg disabled:opacity-40 hover:bg-muted"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((n) => Math.min(totalPages, n + 1))}
                    disabled={page >= totalPages}
                    className="px-3 py-1.5 text-sm border border-border rounded-lg disabled:opacity-40 hover:bg-muted"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      <ReviewDetailModal
        review={openReview}
        isOpen={!!openReview}
        onClose={() => setOpenReview(null)}
        canManage={canManage}
      />
      <TestimonialDetailModal
        testimonial={openTestimonial}
        isOpen={!!openTestimonial}
        onClose={() => setOpenTestimonial(null)}
        canManage={canManage}
      />
    </div>
  );
}
